import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { axiosPost } from "api";
import StockUnitSelectionModal from "components/Operations/StockUnitSelectionModal";
import ObservationRecordsModal, {
  ObservationRecordSavePayload,
} from "components/utils/ObservationRecordsModal";
import {
  MOVEMENT_ARTICLES_GROUPED_OUTCOME_OBSERVATION_PREVIEW_URL,
  MOVEMENT_ARTICLES_GROUPED_OUTCOME_STOCK_UNIT_SELECTION_POOL_URL,
} from "constants/urls";
import { formatDateOnlyDMY } from "helpers";
import {
  buildLocationsById,
  buildParentAndSubLocation,
} from "helpers/articleLocationSelector";
import { getOrderClientFieldConfig } from "helpers/orderClientFields";
import {
  buildObservationPreviewRecords,
  buildObservationPreviewUnitsFromSelectionPool,
  buildSelectionPoolFromPreviewUnits,
  buildStockUnitSelection,
  hasMultipleObservationValues,
  normalizeObservationInput,
  ObservationPreviewUnit,
} from "helpers/stockUnitObservationSelection";
import { useToast } from "hooks/useToast";
import { MdAllInbox, MdBlock, MdVisibility } from "react-icons/md";
import { RxPlus } from "react-icons/rx";
import {
  Client,
  GroupedOutcomeMovementArticle,
  GroupedOutcomeObservationPreviewPayload,
  GroupedOutcomeObservationPreviewStockUnit,
  GroupedOutcomeStockUnitSelectionPoolPayload,
  GroupedOutcomeStockUnitSelectionPoolResponse,
  GroupedOutcomeStockUnitSelectionPoolStockUnit,
  Location,
  OrderDraftArticleRow,
} from "types";

type Props = {
  groupedMovementArticle: GroupedOutcomeMovementArticle;
  client: Client | null;
  locations: Location[];
  existingRows: OrderDraftArticleRow[];
  onAdd: (row: Omit<OrderDraftArticleRow, "localId">) => void;
};

export default function AddOrderArticleRow({
  groupedMovementArticle,
  client,
  locations,
  existingRows,
  onAdd,
}: Props) {
  const { showSnackBar } = useToast();
  const [amount, setAmount] = useState<number>(
    Math.max(0, groupedMovementArticle.available_amount),
  );
  const [observationPreviewUnits, setObservationPreviewUnits] = useState<
    ObservationPreviewUnit[]
  >([]);
  const [observationRecordsOpen, setObservationRecordsOpen] =
    useState<boolean>(false);
  const [observationRecordsLoading, setObservationRecordsLoading] =
    useState<boolean>(false);
  const [stockUnitSelectionPoolOpen, setStockUnitSelectionPoolOpen] =
    useState<boolean>(false);
  const [stockUnitSelectionPoolLoading, setStockUnitSelectionPoolLoading] =
    useState<boolean>(false);
  const [stockUnitSelectionPool, setStockUnitSelectionPool] = useState<
    GroupedOutcomeStockUnitSelectionPoolStockUnit[]
  >([]);

  const clientFieldConfig = getOrderClientFieldConfig(client);
  const showLote = clientFieldConfig.detail.showLote;
  const showOc = clientFieldConfig.detail.showOc;
  const showFechaVencimiento = clientFieldConfig.detail.showFechaVencimiento;

  const articleLocation = groupedMovementArticle.article_locations;

  const locationsById = useMemo(() => {
    return buildLocationsById(locations);
  }, [locations]);

  const parentAndSubLocation = useMemo(() => {
    return buildParentAndSubLocation(articleLocation.location, locationsById);
  }, [articleLocation.location, locationsById]);

  const articleLocationIds = useMemo(
    () => new Set(groupedMovementArticle.article_location_ids),
    [groupedMovementArticle.article_location_ids],
  );

  const rowsInCurrentGroup = useMemo(
    () =>
      existingRows.filter(
        (row) => row.article_id === articleLocation.article.id &&
          articleLocationIds.has(Number(row.article_location_id)),
      ),
    [existingRows, articleLocation.article.id, articleLocationIds],
  );

  const stockUsedInCurrentOrder = rowsInCurrentGroup.reduce(
    (sum, row) => sum + Number(row.amount || 0),
    0,
  );

  const availableUnits = useMemo(
    () =>
      Math.max(
        0,
        groupedMovementArticle.available_amount - stockUsedInCurrentOrder,
      ),
    [groupedMovementArticle.available_amount, stockUsedInCurrentOrder],
  );

  const effectiveReserved = useMemo(
    () => Math.max(0, groupedMovementArticle.reserved_total),
    [groupedMovementArticle.reserved_total],
  );

  const effectiveNotAvailable = useMemo(
    () => Math.max(0, groupedMovementArticle.not_available_total),
    [groupedMovementArticle.not_available_total],
  );
  const inWorkOrders = Math.max(0, Number(groupedMovementArticle.in_work_orders_total ?? 0));

  const alreadyAdded = rowsInCurrentGroup.length > 0;

  const unitsTotal =
    (Number(amount) || 0) * Number(articleLocation.amount_per_container ?? 0);
  const subtotalKg =
    (Number(amount) || 0) * Number(articleLocation.article.weight ?? 0);
  const amountIsInvalid =
    !Number.isInteger(Number(amount)) ||
    Number(amount) <= 0 ||
    Number(amount) > availableUnits;
  const disableAddButton =
    amountIsInvalid || alreadyAdded || stockUnitSelectionPoolLoading;

  const observationPreviewRecords = useMemo(
    () => buildObservationPreviewRecords(observationPreviewUnits),
    [observationPreviewUnits],
  );

  const stockUnitSelection = useMemo(
    () =>
      observationPreviewUnits.length > 0
        ? buildStockUnitSelection(observationPreviewUnits)
        : null,
    [observationPreviewUnits],
  );

  const hasObservationOverrides =
    (stockUnitSelection?.observation_overrides.length ?? 0) > 0;

  const resetObservationPreview = () => {
    setObservationPreviewUnits([]);
    setObservationRecordsOpen(false);
    setObservationRecordsLoading(false);
    setStockUnitSelectionPool([]);
    setStockUnitSelectionPoolOpen(false);
    setStockUnitSelectionPoolLoading(false);
  };

  const buildOrderDraftRowFromPreviewUnits = (
    previewUnits: ObservationPreviewUnit[],
  ) => {
    const previewStockUnitSelection =
      previewUnits.length > 0 ? buildStockUnitSelection(previewUnits) : null;

    onAdd({
      article_id: articleLocation.article.id,
      article_location_id: groupedMovementArticle.article_location_id,
      location_id: articleLocation.location.id,
      location_code: parentAndSubLocation.parent,
      sub_location_code: parentAndSubLocation.sub || "",
      amount: Number(amount),
      amount_per_container: Number(articleLocation.amount_per_container ?? 0),
      lote: articleLocation.lote ?? "",
      oc: articleLocation.oc ?? "",
      fecha_ingreso: articleLocation.fecha_ingreso ?? null,
      fecha_vencimiento: articleLocation.fecha_vencimiento ?? null,
      stock_unit_selection: previewStockUnitSelection ?? undefined,
      observation_preview_units: previewUnits,
    });

    setAmount(1);
  };

  const loadObservationPreviewUnits = async (): Promise<
    ObservationPreviewUnit[] | null
  > => {
    if (!client?.id) {
      showSnackBar("Debe seleccionar un cliente.", "error");
      return null;
    }

    if (amountIsInvalid) {
      showSnackBar(
        `Selecciona una cantidad valida para revisar unidades. Maximo disponible: ${availableUnits}.`,
        "error",
      );
      return null;
    }

    if (observationPreviewUnits.length > 0) {
      return observationPreviewUnits;
    }

    setObservationRecordsLoading(true);

    const response = await axiosPost<GroupedOutcomeObservationPreviewPayload>(
      MOVEMENT_ARTICLES_GROUPED_OUTCOME_OBSERVATION_PREVIEW_URL,
      {
        client_id: client.id,
        article_location_ids: groupedMovementArticle.article_location_ids,
        amount,
      },
      true,
    );

    setObservationRecordsLoading(false);

    if (response.status !== 200) {
      showSnackBar(
        response.data?.message ??
          "No se pudieron obtener las unidades a reservar.",
        "error",
      );
      return null;
    }

    const previewUnits = (
      (response.data ?? []) as GroupedOutcomeObservationPreviewStockUnit[]
    ).map((previewUnit) => ({
      ...previewUnit,
      original_observation: previewUnit.observation ?? null,
    }));

    setObservationPreviewUnits(previewUnits);
    setStockUnitSelectionPool(buildSelectionPoolFromPreviewUnits(previewUnits));
    return previewUnits;
  };

  const loadStockUnitSelectionPool = async (): Promise<
    GroupedOutcomeStockUnitSelectionPoolStockUnit[] | null
  > => {
    if (!client?.id) {
      showSnackBar("Debe seleccionar un cliente.", "error");
      return null;
    }

    if (amountIsInvalid) {
      showSnackBar(
        `Selecciona una cantidad valida para elegir unidades. Maximo disponible: ${availableUnits}.`,
        "error",
      );
      return null;
    }

    if (stockUnitSelectionPool.length > 0) {
      return stockUnitSelectionPool;
    }

    setStockUnitSelectionPoolLoading(true);

    const response = await axiosPost<GroupedOutcomeStockUnitSelectionPoolPayload>(
      MOVEMENT_ARTICLES_GROUPED_OUTCOME_STOCK_UNIT_SELECTION_POOL_URL,
      {
        client_id: client.id,
        article_location_ids: groupedMovementArticle.article_location_ids,
        amount,
      },
      true,
    );

    setStockUnitSelectionPoolLoading(false);

    if (response.status !== 200) {
      showSnackBar(
        response.data?.message ??
          "No se pudo obtener el pool de observaciones para reservar.",
        "error",
      );
      return null;
    }

    const nextStockUnitSelectionPool =
      (response.data as GroupedOutcomeStockUnitSelectionPoolResponse | undefined)
        ?.stock_units ?? [];

    setStockUnitSelectionPool(nextStockUnitSelectionPool);
    return nextStockUnitSelectionPool;
  };

  const handleOpenObservationRecords = async () => {
    const previewUnits = await loadObservationPreviewUnits();

    if (!previewUnits) {
      return;
    }

    setObservationRecordsOpen(true);
  };

  const handleSaveObservationRecord = async ({
    recordId,
    observation,
    quantityMode,
    specificQuantity,
  }: ObservationRecordSavePayload) => {
    const targetRecord = observationPreviewRecords.find(
      (record) => record.id === recordId,
    );

    if (!targetRecord) {
      showSnackBar(
        "No se encontro el grupo de observaciones seleccionado.",
        "error",
      );
      return;
    }

    const targetQuantity =
      quantityMode === "specific"
        ? Number(specificQuantity ?? 0)
        : targetRecord.stock_unit_ids.length;

    if (
      !Number.isFinite(targetQuantity) ||
      targetQuantity <= 0 ||
      targetQuantity > targetRecord.stock_unit_ids.length
    ) {
      showSnackBar("La cantidad especifica seleccionada no es valida.", "error");
      return;
    }

    const nextObservation = normalizeObservationInput(observation);
    const affectedStockUnitIds = new Set(
      targetRecord.stock_unit_ids.slice(0, targetQuantity),
    );

    setObservationPreviewUnits((currentPreviewUnits) =>
      currentPreviewUnits.map((previewUnit) =>
        affectedStockUnitIds.has(previewUnit.id)
          ? {
              ...previewUnit,
              observation: nextObservation,
            }
          : previewUnit,
      ),
    );

    setStockUnitSelectionPool((currentStockUnitSelectionPool) =>
      currentStockUnitSelectionPool.map((stockUnit) =>
        affectedStockUnitIds.has(stockUnit.id)
          ? {
              ...stockUnit,
              observation: nextObservation,
            }
          : stockUnit,
      ),
    );
  };

  const handleSubmit = async () => {
    if (amountIsInvalid) {
      showSnackBar(
        `La cantidad solicitada no puede superar el disponible (${availableUnits}).`,
        "error",
      );
      return;
    }

    const selectableStockUnits =
      stockUnitSelectionPool.length > 0
        ? stockUnitSelectionPool
        : await loadStockUnitSelectionPool();

    if (!selectableStockUnits) {
      return;
    }

    if (hasMultipleObservationValues(selectableStockUnits)) {
      setStockUnitSelectionPoolOpen(true);
      return;
    }

    const automaticPreviewSource =
      observationPreviewUnits.length > 0
        ? observationPreviewUnits
        : buildObservationPreviewUnitsFromSelectionPool(selectableStockUnits);

    buildOrderDraftRowFromPreviewUnits(automaticPreviewSource.slice(0, amount));
  };

  useEffect(() => {
    resetObservationPreview();
  }, [amount, groupedMovementArticle.group_key]);

  useEffect(() => {
    setAmount(Math.max(0, availableUnits));
  }, [groupedMovementArticle.group_key, availableUnits]);

  return (
    <>
      <TableRow>
        <TableCell align="center">{articleLocation.article.code}</TableCell>
        <TableCell align="center">{articleLocation.article.description}</TableCell>
        <TableCell align="center">
          {articleLocation.article.units_per_pallet ?? "-"}
        </TableCell>
        <TableCell align="center">
          {articleLocation.article.unit.description ?? "-"}
        </TableCell>
        <TableCell align="center">
          {articleLocation.article.presentation ?? "-"}
        </TableCell>
        <TableCell align="center">
          <TextField
            label="Cantidad"
            type="number"
            size="small"
            value={amount}
            onChange={(event) =>
              setAmount(Math.max(0, Number(event.target.value) || 0))
            }
            error={amountIsInvalid}
            helperText={amountIsInvalid ? `Maximo ${availableUnits}` : undefined}
            inputProps={{ min: 0, max: availableUnits }}
          />
        </TableCell>

        <TableCell align="center">
          {articleLocation.amount_per_container ?? "-"}
        </TableCell>

        {showLote && (
          <TableCell align="center">{articleLocation.lote || "-"}</TableCell>
        )}

        <TableCell align="center">
          {articleLocation.fecha_ingreso
            ? formatDateOnlyDMY(articleLocation.fecha_ingreso)
            : "-"}
        </TableCell>

        <TableCell align="center">{parentAndSubLocation.parent}</TableCell>
        <TableCell align="center">{parentAndSubLocation.sub || "-"}</TableCell>

        {showOc && <TableCell align="center">{articleLocation.oc || "-"}</TableCell>}

        {showFechaVencimiento && (
          <TableCell align="center">
            {articleLocation.fecha_vencimiento
              ? formatDateOnlyDMY(articleLocation.fecha_vencimiento)
              : "-"}
          </TableCell>
        )}

        <TableCell align="center">{unitsTotal}</TableCell>
        <TableCell align="center">{subtotalKg}</TableCell>
        <TableCell align="center">
          <Tooltip
            title={
              amountIsInvalid
                ? "Selecciona primero una cantidad valida para ver las observaciones."
                : hasObservationOverrides
                  ? "Editar observaciones seleccionadas"
                  : "Ver unidades a reservar"
            }
          >
            <span>
              <IconButton
                color={hasObservationOverrides ? "primary" : "default"}
                disabled={amountIsInvalid || observationRecordsLoading}
                onClick={() => void handleOpenObservationRecords()}
              >
                {observationRecordsLoading ? (
                  <CircularProgress size={18} />
                ) : (
                  <MdVisibility />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
        <TableCell align="center">
          <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
            <Typography noWrap>{availableUnits}</Typography>
            <Typography noWrap display="flex" alignItems="center" gap={0.4}>
              ({effectiveReserved} <MdAllInbox />)
            </Typography>
            <Typography
              noWrap
              display="flex"
              alignItems="center"
              gap={0.4}
              color="error.main"
            >
              ({effectiveNotAvailable} <MdBlock />)
            </Typography>
            <Typography noWrap>
              ({inWorkOrders} En OT)
            </Typography>
          </Box>
        </TableCell>

        <TableCell align="center">
          <Tooltip
            disableHoverListener={!disableAddButton}
            title={
              alreadyAdded
                ? "Esta linea agrupada ya fue agregada al pedido"
                : amountIsInvalid
                  ? "La cantidad seleccionada supera el stock disponible"
                  : stockUnitSelectionPoolLoading
                    ? "Cargando unidades disponibles"
                    : ""
            }
          >
            <Box>
              <Button
                type="button"
                aria-label="Agregar articulo al pedido"
                variant="contained"
                disabled={disableAddButton}
                onClick={() => void handleSubmit()}
              >
                <RxPlus size="1.2rem" />
              </Button>
            </Box>
          </Tooltip>
        </TableCell>
      </TableRow>

      <ObservationRecordsModal
        open={observationRecordsOpen}
        loading={observationRecordsLoading}
        title="Unidades a reservar"
        subtitle={`${articleLocation.article.code} - ${articleLocation.article.description} | Cantidad a reservar: ${amount} | Pool elegible completo`}
        records={observationPreviewRecords}
        onClose={() => setObservationRecordsOpen(false)}
        onSaveRecord={handleSaveObservationRecord}
      />

      <StockUnitSelectionModal
        open={stockUnitSelectionPoolOpen}
        amount={amount}
        stockUnits={stockUnitSelectionPool}
        title="Seleccionar unidades a reservar"
        subtitle={`${articleLocation.article.code} - ${articleLocation.article.description} | Cantidad seleccionada: ${amount}`}
        quantityLabel="Cantidad a reservar"
        availabilityLabel="Disponible para esta observación"
        confirmLabel="Confirmar selección"
        onClose={() => setStockUnitSelectionPoolOpen(false)}
        onConfirm={(selectedStockUnitIds) => {
          const selectedPreviewUnits = (
            observationPreviewUnits.length > 0
              ? observationPreviewUnits
              : buildObservationPreviewUnitsFromSelectionPool(
                  stockUnitSelectionPool,
                )
          ).filter((previewUnit) => selectedStockUnitIds.includes(previewUnit.id));

          setObservationPreviewUnits(selectedPreviewUnits);
          setStockUnitSelectionPoolOpen(false);
          buildOrderDraftRowFromPreviewUnits(selectedPreviewUnits);
        }}
      />
    </>
  );
}
